import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { BusinessAddress } from '../../types'

type Country = { id: string; code: string; name: string }
type StateProvince = { id: string; code: string; name: string; country_id: string }

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  address: BusinessAddress | null
  basePath: string
  countries: Country[]
  states: StateProvince[]
}

export function BusinessAddressDialog({ open, onOpenChange, businessId, address, basePath, countries, states }: Props) {
  const [apiError, setApiError] = useState<string | null>(null)
  const [selectedCountryId, setSelectedCountryId] = useState<string>('')

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    label: '', street_address_1: '', street_address_2: '', city: '', state_province: '', postal_code: '', country_code: ''
  })

  const filteredStates = selectedCountryId ? states.filter(s => s.country_id === selectedCountryId) : []

  useEffect(() => {
    if (open) {
      if (address) {
        setData({
          label: address.label || '', street_address_1: address.street_address_1,
          street_address_2: address.street_address_2 || '', city: address.city,
          state_province: address.state_province || '', postal_code: address.postal_code || '',
          country_code: address.country_code
        })
        const country = countries.find(c => c.code === address.country_code)
        setSelectedCountryId(country?.id || '')
      } else { reset(); setSelectedCountryId('') }
      setApiError(null); clearErrors()
    }
  }, [open, address])

  function handleCountryChange(countryCode: string) {
    setData({ ...data, country_code: countryCode, state_province: '' })
    const country = countries.find(c => c.code === countryCode)
    setSelectedCountryId(country?.id || '')
  }

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) { reset(); clearErrors() }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setApiError(null)
    const resourcePath = `${basePath}/businesses/${businessId}/addresses`
    if (address?.id) {
      patch(`${resourcePath}/${address.id}`, { onSuccess: () => handleClose(true), onError: (errors) => setApiError(Object.values(errors).join(', ')) })
    } else {
      post(resourcePath, { onSuccess: () => handleClose(true), onError: (errors) => setApiError(Object.values(errors).join(', ')) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-6"><DialogTitle>{address ? 'Edit Address' : 'Add Address'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {apiError && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{apiError}</div>}
          <div className="space-y-2">
            <Label htmlFor="label">Label <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input id="label" placeholder="e.g. Head Office" value={data.label} onChange={e => setData('label', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="street_address_1">Street Address <span className="text-destructive">*</span></Label>
            <Input id="street_address_1" placeholder="123 Main Street" value={data.street_address_1} onChange={e => setData('street_address_1', e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="street_address_2">Suite / Unit <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input id="street_address_2" placeholder="Suite 100" value={data.street_address_2} onChange={e => setData('street_address_2', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City <span className="text-destructive">*</span></Label>
            <Input id="city" placeholder="New York" value={data.city} onChange={e => setData('city', e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country_code">Country <span className="text-destructive">*</span></Label>
            <CountryCombobox
              id="country_code"
              countries={countries}
              value={data.country_code}
              onValueChange={handleCountryChange}
              placeholder="Select a country"
            />
          </div>
          {filteredStates.length > 0 ? (
            <div className="space-y-2">
              <Label htmlFor="state_province">State / Province</Label>
              <Select value={data.state_province || undefined} onValueChange={val => setData('state_province', val)}>
                <SelectTrigger id="state_province"><SelectValue placeholder="Select a state..." /></SelectTrigger>
                <SelectContent>
                  {filteredStates.map(s => <SelectItem key={s.id} value={s.code}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="state_province">State / Province</Label>
              <Input id="state_province" placeholder="e.g. NY, Lagos" value={data.state_province} onChange={e => setData('state_province', e.target.value)} />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input id="postal_code" placeholder="10001" value={data.postal_code} onChange={e => setData('postal_code', e.target.value)} />
          </div>
          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={processing}>{processing ? 'Saving...' : address ? 'Update' : 'Add Address'}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
